import { Node, mergeAttributes } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { BackgroundColor, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ContentEditorEmailImageView from './ContentEditorEmailImageView.jsx';
import AiField from '../template-variables/AiField.js';
import TemplateVariable from '../template-variables/TemplateVariable.js';
import {
  EMAIL_ALIGNMENTS,
  EMAIL_BUTTON_DESTINATION_TYPES,
  EMAIL_BUTTON_ICON_POSITIONS,
  EMAIL_BUTTON_ICONS,
  EMAIL_BUTTON_SIZES,
  EMAIL_BUTTON_STYLES,
  EMAIL_CALLOUT_TONES,
  EMAIL_DIVIDER_STYLES,
  EMAIL_IMAGE_WIDTH_PRESETS,
  EMAIL_SPACINGS,
  EMAIL_WIDTHS,
  normalizeEmailButtonAttrs,
} from '@mts/shared/emailEditorProfile';

function enumValue(value, allowed, fallback) {
  return allowed.includes(value) ? value : fallback;
}

function buttonIconGlyph(icon) {
  return {
    'arrow-right': '→',
    'external-link': '↗',
    download: '↓',
    calendar: '📅',
    phone: '☎',
    email: '✉',
  }[icon] || '';
}

function buttonDestinationPreview(attrs) {
  if (attrs.destinationType === 'VARIABLE') return attrs.hrefVariable ? `{{${attrs.hrefVariable}}}` : 'Destination variable missing';
  if (attrs.destinationType === 'EMAIL') return attrs.href ? `mailto:${attrs.href.replace(/^mailto:/i, '')}` : 'Email missing';
  if (attrs.destinationType === 'PHONE') return attrs.href ? `tel:${attrs.href.replace(/^tel:/i, '')}` : 'Phone missing';
  return attrs.href || 'URL missing';
}

function EmailButtonView({ editor, getPos, node, selected }) {
  const attrs = normalizeEmailButtonAttrs(node.attrs || {});
  const destination = buttonDestinationPreview(attrs);
  const icon = buttonIconGlyph(attrs.icon);

  const selectNode = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (typeof pos === 'number') editor?.chain().focus().setNodeSelection(pos).run();
  };

  return (
    <NodeViewWrapper
      as="div"
      data-email-button
      data-destination-type={attrs.destinationType}
      className={[
        'email-node email-button-node',
        `email-button-node--${attrs.alignment}`,
        selected ? 'email-node--selected' : '',
      ].join(' ')}
      onClick={selectNode}
      onMouseDown={selectNode}
    >
      <span
        className={[
          'email-button-preview',
          `email-button-preview--${attrs.style}`,
          `email-button-preview--${attrs.width}`,
          `email-button-preview--${attrs.size}`,
        ].join(' ')}
      >
        {attrs.iconPosition === 'before' && icon ? <span aria-hidden="true">{icon}</span> : null}
        <span>{attrs.label || 'Open'}</span>
        {attrs.iconPosition !== 'before' && icon ? <span aria-hidden="true">{icon}</span> : null}
      </span>
      <span className="email-button-node__meta">
        {attrs.destinationType} · {destination}
      </span>
    </NodeViewWrapper>
  );
}

export const MergeVariable = Node.create({
  name: 'mergeVariable',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      name: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-merge-variable]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-merge-variable': node.attrs.name,
      class: 'merge-variable-chip',
    }), `{{${node.attrs.name}}}`];
  },
  renderText({ node }) {
    return `{{${node.attrs.name}}}`;
  },
  addCommands() {
    return {
      insertMergeVariable: (name) => ({ commands }) => commands.insertContent({ type: this.name, attrs: { name } }),
    };
  },
});

export const ObjectVariable = Node.create({
  name: 'objectVariable',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      objectKey: { default: null },
      propertyKey: { default: null },
      objectLabel: { default: null },
      propertyLabel: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-object-variable]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const path = `${node.attrs.objectKey}.${node.attrs.propertyKey}`;
    const label = `${node.attrs.objectLabel || node.attrs.objectKey} › ${node.attrs.propertyLabel || node.attrs.propertyKey}`;
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-object-variable': path,
      class: 'object-variable-chip',
    }), label];
  },
  renderText({ node }) {
    return `{{${node.attrs.objectKey}.${node.attrs.propertyKey}}}`;
  },
  addCommands() {
    return {
      insertObjectVariable: (attrs) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export const EmailButton = Node.create({
  name: 'emailButton',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      label: { default: 'Open' },
      destinationType: { default: 'URL' },
      href: { default: '' },
      hrefVariable: { default: '' },
      style: { default: 'primary' },
      alignment: { default: 'left' },
      width: { default: 'auto' },
      size: { default: 'medium' },
      icon: { default: 'none' },
      iconPosition: { default: 'after' },
      accessibleLabel: { default: '' },
      trackingLabel: { default: '' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-email-button]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const attrs = normalizeEmailButtonAttrs(node.attrs || {});
    const alignment = enumValue(attrs.alignment, EMAIL_ALIGNMENTS, 'left');
    const style = enumValue(attrs.style, EMAIL_BUTTON_STYLES, 'primary');
    const width = enumValue(attrs.width, EMAIL_WIDTHS, 'auto');
    const size = enumValue(attrs.size, EMAIL_BUTTON_SIZES, 'medium');
    const destinationType = enumValue(attrs.destinationType, EMAIL_BUTTON_DESTINATION_TYPES, 'URL');
    const icon = enumValue(attrs.icon, EMAIL_BUTTON_ICONS, 'none');
    const iconPosition = enumValue(attrs.iconPosition, EMAIL_BUTTON_ICON_POSITIONS, 'after');
    const destination = buttonDestinationPreview(attrs);
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-email-button': '',
      'data-style': style,
      'data-alignment': alignment,
      'data-width': width,
      'data-size': size,
      'data-destination-type': destinationType,
      'data-icon': icon,
      'data-icon-position': iconPosition,
      class: `email-node email-button-node email-button-node--${alignment}`,
    }), [
      'span',
      {
        class: `email-button-preview email-button-preview--${style} email-button-preview--${width} email-button-preview--${size}`,
      },
      `${attrs.label || 'Open'}${destination ? ` → ${destination}` : ''}`,
    ]];
  },
  addNodeView() {
    return ReactNodeViewRenderer(EmailButtonView);
  },
  addCommands() {
    return {
      insertEmailButton: (attrs = {}) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export const EmailImage = Node.create({
  name: 'emailImage',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: '' },
      assetId: { default: '' },
      alt: { default: '' },
      href: { default: '' },
      alignment: { default: 'center' },
      widthPreset: { default: 'full' },
      caption: { default: '' },
    };
  },
  parseHTML() {
    return [{ tag: 'figure[data-email-image]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const alignment = enumValue(node.attrs.alignment, EMAIL_ALIGNMENTS, 'center');
    const widthPreset = enumValue(node.attrs.widthPreset, EMAIL_IMAGE_WIDTH_PRESETS, 'full');
    return ['figure', mergeAttributes(HTMLAttributes, {
      'data-email-image': '',
      'data-alignment': alignment,
      'data-width-preset': widthPreset,
      class: `email-node email-image-node email-image-node--${alignment}`,
    }), [
      'div',
      { class: 'email-image-placeholder' },
      node.attrs.src ? `Image: ${node.attrs.alt || node.attrs.src}` : 'Email image placeholder',
    ], node.attrs.caption ? ['figcaption', { class: 'email-image-caption' }, node.attrs.caption] : ['figcaption', { class: 'email-image-caption' }, '']];
  },
  addNodeView() { return ReactNodeViewRenderer(ContentEditorEmailImageView); },
  addCommands() {
    return {
      insertEmailImage: (attrs = {}) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export const EmailDivider = Node.create({
  name: 'emailDivider',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      style: { default: 'standard' },
      spacing: { default: 'medium' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-email-divider]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const style = enumValue(node.attrs.style, EMAIL_DIVIDER_STYLES, 'standard');
    const spacing = enumValue(node.attrs.spacing, EMAIL_SPACINGS, 'medium');
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-email-divider': '',
      'data-style': style,
      'data-spacing': spacing,
      class: `email-node email-divider-node email-divider-node--${style} email-divider-node--${spacing}`,
    })];
  },
  addCommands() {
    return {
      insertEmailDivider: (attrs = {}) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export const EmailSpacer = Node.create({
  name: 'emailSpacer',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      size: { default: 'medium' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-email-spacer]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const size = enumValue(node.attrs.size, EMAIL_SPACINGS, 'medium');
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-email-spacer': '',
      'data-size': size,
      class: `email-node email-spacer-node email-spacer-node--${size}`,
    }), `Spacer: ${size}`];
  },
  addCommands() {
    return {
      insertEmailSpacer: (attrs = {}) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export const EmailCallout = Node.create({
  name: 'emailCallout',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() {
    return {
      tone: { default: 'neutral' },
    };
  },
  parseHTML() {
    return [{ tag: 'aside[data-email-callout]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const tone = enumValue(node.attrs.tone, EMAIL_CALLOUT_TONES, 'neutral');
    return ['aside', mergeAttributes(HTMLAttributes, {
      'data-email-callout': '',
      'data-tone': tone,
      class: `email-node email-callout-node email-callout-node--${tone}`,
    }), 0];
  },
  addCommands() {
    return {
      insertEmailCallout: (attrs = {}) => ({ commands }) => commands.insertContent({
        type: this.name,
        attrs,
        content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Add callout copy.' }] }],
      }),
    };
  },
});

export function createEmailEditorExtensions(profile) {
  const allowImages = profile?.features?.allowImages !== false;
  const allowButtons = profile?.features?.allowButtons !== false;

  return [
    StarterKit.configure({
      heading: { levels: [1, 2] },
      blockquote: false,
      codeBlock: false,
      horizontalRule: false,
      hardBreak: false,
      link: false,
      underline: false,
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        rel: 'noopener noreferrer',
        class: 'text-blue-700 underline underline-offset-2',
      },
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right'],
      defaultAlignment: 'left',
    }),
    TextStyle,
    FontFamily.configure({ types: ['textStyle'] }),
    FontSize.configure({ types: ['textStyle'] }),
    Color.configure({ types: ['textStyle'] }),
    BackgroundColor.configure({ types: ['textStyle'] }),
    MergeVariable,
    ObjectVariable,
    TemplateVariable,
    AiField,
    allowButtons ? EmailButton : null,
    allowImages ? EmailImage : null,
    EmailDivider,
    EmailSpacer,
    EmailCallout,
  ].filter(Boolean);
}
